<script setup lang="ts">
import { ref, watch, toRefs, toRef, computed, onMounted, useSlots } from 'vue';
import { debounce } from '@/utils/index';
const emit = defineEmits(['update:modelValue', 'change', 'input']);

// interface List {
//     id: number | string;
//     label: string;
// }

// 定义 props 类型
interface Props {
  modelValue: number | string | null; //当前值
  class?: string;
  fontSize?: number; // 字体大小
  list: Array<any>; // 列表
  disabled?: boolean; // 是否禁用
  width?: number;
  height?: number;
  placeholder?: string;
  valueKey?: string; //值的key（如果没有，则数组的项是非对象）
  labelKey?: string;
  filterable?: boolean; //是否开启搜索功能
  pickerFontsize?: number; //选择框字体大小
  searchDefault?: string; //搜索框默认值
  noDataTest?: string; //无数据提示文字
  isShowClear?: boolean;
}

// 使用 withDefaults 设置默认值
const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  class: '',
  fontSize: 12,
  list: () => [],
  disabled: false,
  width: 180,
  height: 28,
  placeholder: '请选择',
  valueKey: '',
  labelKey: '',
  filterable: false,
  pickerFontsize: 12,
  searchDefault: '',
  noDataTest: '无搜索结果~',
  isShowClear: false,
});

const isObjectItem = (item: any) => item !== null && typeof item === 'object';
const getItemValue = (item: any) =>
  isObjectItem(item) && valueKey.value ? item[valueKey.value] : item;
const getItemLabel = (item: any) =>
  isObjectItem(item) && labelKey.value
    ? item[labelKey.value]
    : isObjectItem(item)
    ? String(item)
    : String(item);

const updateCurrentLabelByValue = (val: any) => {
  if (val === null || val === undefined || val === '') {
    currentLabel.value = '';
    return;
  }
  // try to find matching item in list
  const found = list.value.find((it: any) => {
    const v = getItemValue(it);
    // compare as string/number
    return String(v) === String(val);
  });
  if (found) {
    currentLabel.value = getItemLabel(found);
    temporaryLabel.value = currentLabel.value;
    searchValue.value = currentLabel.value;
  } else {
    // fallback: if valueKey not set and value is primitive, show value directly
    currentLabel.value = String(val) || '';
    temporaryLabel.value = currentLabel.value || val;
    searchValue.value = currentLabel.value || val;
  }
};

const {
  modelValue,
  fontSize,
  list,
  disabled,
  valueKey,
  labelKey,
  filterable,
  pickerFontsize,
  searchDefault,
  isShowClear,
} = toRefs(props);
const rootClass = toRef(props, 'class');

const slots = useSlots();
const hasPopoverHeader = computed(() => !!slots.popoverHeader);

const currentLabel: any = ref('');
const isShowPopover = ref(false); //是否显示下拉框
const pickerHoverIndex: any = ref(null);
const searchValue = ref(''); //搜索框的值
const temporaryLabel = ref(''); //临时保存的值

const currentValue = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    emit('update:modelValue', newValue);
    updateCurrentLabelByValue(newValue);
  },
});

watch(
  () => props.modelValue,
  (newVal) => {
    updateCurrentLabelByValue(newVal);
  },
  { immediate: true }
);

watch(
  list,
  () => {
    updateCurrentLabelByValue(props.modelValue);
  },
  { deep: true }
);

//回填值
watch(searchDefault, (newValue) => {
  if (newValue) {
    temporaryLabel.value = newValue;
    searchValue.value = newValue;
  }
});

// 选中
const chooseItem = (item: any) => {
  const value = getItemValue(item);
  const label = getItemLabel(item);
  // ignore disabled items (list item may carry disabled flag)
  if (isObjectItem(item) && item.disabled === true) return;
  currentValue.value = value;
  currentLabel.value = label;
  searchValue.value = label;
  temporaryLabel.value = label;
  isShowPopover.value = false;
  emit('change', { value, label });
  emit('update:modelValue', value);
};

const hideComponent = () => {
  isShowPopover.value = false;
};

const togglePopover = () => {
  if (disabled.value) return;
  isShowPopover.value = !isShowPopover.value;
};

//清除数据
const clearData = () => {
  currentValue.value = '';
  currentLabel.value = '';
  searchValue.value = '';
  temporaryLabel.value = '';
  isShowPopover.value = false;
  emit('change', { value: '', label: '' });
  emit('update:modelValue', '');
};

//鼠标移入
const mouseEnterPicker = (index: number) => {
  pickerHoverIndex.value = index;
};

//鼠标移出
const mouseLeavePicker = () => {
  pickerHoverIndex.value = null;
};

//搜索框输入
const hangdleInput = debounce((e: any) => {
  isShowPopover.value = true;
  searchValue.value = e.target.value;
  emit('input', e.target.value);
}, 500);

//搜索框失去焦点
const hangdleBlur = () => {
  if (modelValue.value) {
    searchValue.value = temporaryLabel.value;
  } else {
    searchValue.value = '';
  }
};

//搜索框聚焦：清空关键词并通知父组件拉全量列表（避免仍按旧关键词过滤导致下拉为空）
const hangdleFocus = () => {
  searchValue.value = '';
  emit('input', '');
};

onMounted(() => {
  updateCurrentLabelByValue(props.modelValue);
});
</script>

<template>
  <div :class="rootClass" class="fr-ac pos-r" v-click-outside="hideComponent">
    <div
      class="fr-ac-jsb picker-box"
      :style="{
        width: width + 'px',
        height: height + 'px',
        fontSize: fontSize + 'px',
      }"
      :class="{ opa4: disabled }"
      @click="togglePopover"
    >
      <input
        class="custom-input"
        :placeholder="placeholder"
        v-if="filterable"
        type="text"
        :value="searchValue"
        :disabled="disabled"
        @click.stop
        @input="hangdleInput"
        @blur="hangdleBlur"
        @focus="hangdleFocus"
      />
      <div
        v-else
        class="custom-input"
        :class="currentLabel ? '' : 'placeholder'"
      >
        {{ currentLabel ? currentLabel : placeholder }}
      </div>
      <div lass="ml-5">
        <IconFont
          @click.stop="clearData"
          class="mr-10"
          v-if="isShowClear && currentLabel"
          name="close"
          :size="14"
          color="#2a2a2a"
        />
        <IconFont
          :class="isShowPopover ? 'icon-pull-active' : 'icon-pull-unactive'"
          name="icon_pull"
          :size="10"
          color="#2a2a2a"
        />
      </div>
    </div>
    <div
      class="popover-box"
      :class="{ 'popover-box--with-header': hasPopoverHeader }"
      :style="{ width: width + 'px', top: height ? height + 'px' : '38px' }"
      v-if="isShowPopover"
    >
      <div
        v-if="hasPopoverHeader"
        class="popover-header"
        @mousedown.stop
        @click.stop
      >
        <slot name="popoverHeader"></slot>
      </div>
      <div
        class="scroll-container w-per-100 pr-6 popover-scroll"
        v-if="list && list.length"
      >
        <div
          class="fr-ac mb-4 pointer"
          :class="
            item.disabled
              ? 'picker-disabled'
              : pickerHoverIndex === index
              ? 'picker-hover'
              : ''
          "
          v-for="(item, index) in list"
          :key="index"
          @mouseenter="mouseEnterPicker(index)"
          @mouseleave="mouseLeavePicker()"
          @click="chooseItem(item)"
        >
          <div
            class="picker-item fr-ac"
            style="flex-shrink: 0"
            :style="{ 'font-size': pickerFontsize + 'px' }"
          >
            {{ labelKey ? item[labelKey] : item }}
          </div>
        </div>
      </div>
      <div
        v-else
        class="fc-ac-jc w-per-100 pb-8 popover-scroll popover-scroll--empty"
      >
        <img
          class="w-58 h-56"
          src="@/assets/images/empty/icon_error3.png"
          alt=""
        />
        <span class="opa8 fs-12 mt-12">{{ noDataTest }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.picker-box {
  height: 28px;
  border-radius: var(--border-radius);
  padding: 0 16px;
  background: var(--input-bg-color);
  cursor: pointer;

  .custom-input {
    // width: 135px;
    /* 使用 CSS 变量 */
    font-size: var(--placeholder-font-size);
  }

  .custom-input::placeholder {
    color: var(--input-placeholder-color);
  }

  .placeholder {
    color: var(--input-placeholder-color);
    font-size: var(--placeholder-font-size);
  }
}

.icon-pull-active {
  animation: rotate180 0.5s forwards;
}

.icon-pull-unactive {
  animation: rotate-180 0.5s forwards;
}

@keyframes rotate180 {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(180deg);
  }
}

@keyframes rotate-180 {
  from {
    transform: rotate(180deg);
  }

  to {
    transform: rotate(0deg);
  }
}

.popover-box {
  position: absolute;
  z-index: 9;
  left: 50%;
  height: 140px;
  transform: translateX(-50%);
  padding: 12px 12px 8px;
  border-radius: var(--border-radius);
  background: var(--bg-color-white);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--selector-box-shadow);
  // background: linear-gradient(180deg, #111B37 0%, #091023 100%);
}

.popover-box--with-header {
  height: auto;
  max-height: 220px;
  min-height: 140px;
}

.popover-header {
  flex-shrink: 0;
  margin: -4px 0 8px;
  width: 100%;
}

.popover-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.popover-scroll--empty {
  min-height: 100px;
  flex: 1;
}

.picker-item {
  width: 100%;
  font-size: 12px;
  border-radius: var(--border-radius);
  padding: 5px 10px;
}

.picker-disabled {
  color: #999999;
  background: rgba(55, 133, 255, 0.1);
  border-radius: var(--border-radius);
}

.picker-hover {
  background: rgba(55, 133, 255, 0.1);
}
</style>
